import React, { useState, useCallback, useMemo } from "react";
import { useAllItemsQuery } from "../../../Hooks/useAllItemsQuery";
import isAuthenticated from "../../../components/Utils/isAuthenticated";
import CustomList from "./CustomList";

export default function BlockItemsTransfer(props) {
	// Helper functions for list mathematics
	const not = (a, b) => a.filter((value) => b.indexOf(value) === -1);
	const intersection = (a, b) => a.filter((value) => b.indexOf(value) !== -1);
	const union = useCallback((a, b) => [...a, ...not(b, a)], []);

	const auth = isAuthenticated();
	const token = auth.token;
	const [checked, setChecked] = useState([]);

	// Fetch and remap the API data
	const { data: allItemsData } = useAllItemsQuery(token);
	const allItems = useMemo(() => {
		if (!allItemsData || allItemsData.length === 0) return [];
		return allItemsData.map((item) => ({
			itemno: item.itemno,
			text: item.text,
			cat_id: item.cat_id,
		}));
	}, [allItemsData]);

	// Compute available items (left list) by filtering allItems by desired condition
	const availableItems = useMemo(() => {
		return allItems
			.filter(
				(item) =>
					item.cat_id !== 10 &&
					item.itemno < 1000 &&
					!props.chosenItems.some((chosen) => chosen.itemno === item.itemno)
			)
			.sort((a, b) => a.itemno - b.itemno);
	}, [allItems, props.chosenItems]);

	// Compute checked items intersections for both lists
	const leftChecked = useMemo(
		() => intersection(checked, availableItems),
		[checked, availableItems]
	);
	const rightChecked = useMemo(
		() => intersection(checked, props.chosenItems),
		[checked, props.chosenItems]
	);

	// Handler to toggle a single item in the checked array
	const handleToggle = useCallback(
		(value) => () => {
			setChecked((prev) => {
				const currentIndex = prev.indexOf(value);
				if (currentIndex === -1) {
					return [...prev, value];
				} else {
					return prev.filter((item) => item !== value);
				}
			});
		},
		[]
	);

	const numberOfChecked = useCallback((items) => intersection(checked, items).length, [checked]);

	const handleToggleAll = useCallback(
		(items) => () => {
			if (numberOfChecked(items) === items.length) {
				setChecked((prev) => not(prev, items));
			} else {
				setChecked((prev) => union(prev, items));
			}
		},
		[numberOfChecked, union]
	);

	// Move checked items from available to chosen
	const handleCheckedRight = useCallback(() => {
		props.setChosenItems([...props.chosenItems, ...leftChecked]);
		setChecked((prev) => not(prev, leftChecked));
	}, [props, leftChecked]);

	// Move checked items from chosen back to available
	const handleCheckedLeft = useCallback(() => {
		const newChosen = not(props.chosenItems, rightChecked);
		props.setChosenItems(newChosen);
		setChecked((prev) => not(prev, rightChecked));
	}, [props, rightChecked]);

	// A custom list component rendered with plain HTML.
	// It shows a header with a "select all" checkbox, a title, and a count,
	// plus an unordered list of items.

	return (
		<div
			style={{
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				gap: "16px",
			}}
		>
			<div>
				<CustomList
					title="Items"
					items={availableItems}
					checked={checked}
					onToggle={handleToggle}
					onToggleAll={handleToggleAll}
					numberOfChecked={numberOfChecked}
				/>
			</div>
			<div className="   gap-2 flex flex-col items-center justify-center">
				<button
					onClick={handleCheckedRight}
					disabled={leftChecked.length === 0}
					aria-label="move selected right"
					className={`hover:bg-darkGreen hover:cursor-pointer px-2 bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center disabled:bg-gray-400`}
				>
					&gt;
				</button>
				<button
					onClick={handleCheckedLeft}
					disabled={rightChecked.length === 0}
					aria-label="move selected left"
					className={`hover:bg-darkGreen hover:cursor-pointer px-2 bg-primary text-white rounded-full w-12 h-12 flex items-center justify-center disabled:bg-gray-400`}
				>
					&lt;
				</button>
			</div>
			<div>
				<CustomList
					title="Items Chosen"
					items={props.chosenItems}
					checked={checked}
					onToggle={handleToggle}
					onToggleAll={handleToggleAll}
					numberOfChecked={numberOfChecked}
				/>
			</div>
		</div>
	);
}
